import { useRef, useState } from 'react'
import { env } from '../lib/env.js'
import { Button } from './Button.js'

interface ImageUploadProps {
  /** Current stored image URL, if any. */
  value: string | null
  onChange: (url: string | null) => void
  /** Label for the upload button. */
  label?: string
  /** `capture` hints mobile browsers to open the camera directly. */
  capture?: boolean
}

/**
 * Uploads a single image to the API's R2-backed /uploads endpoint and reports
 * the stored URL. Camera-capable on mobile via the `capture` attribute. Used
 * for the student profile photo and academic-history report-card proofs.
 */
export function ImageUpload({ value, onChange, label = 'Upload image', capture }: ImageUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const onPick = async (file: File) => {
    setBusy(true)
    setError(null)
    try {
      const form = new FormData()
      form.append('file', file)
      const res = await fetch(`${env.API_URL}/api/v1/uploads`, {
        method: 'POST',
        // Cookie-based auth: send the session cookie cross-subdomain. Don't set
        // Content-Type — the browser sets the multipart boundary for FormData.
        credentials: 'include',
        body: form,
      })
      const json = (await res.json()) as
        | { data: { url: string } }
        | { error: { message: string } }
      if (!res.ok || 'error' in json) {
        throw new Error('error' in json ? json.error.message : 'Upload failed')
      }
      onChange(json.data.url)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-2">
      {value ? (
        <div className="flex items-center gap-3">
          <img
            src={value}
            alt="Uploaded preview"
            className="h-16 w-16 rounded-md border border-border object-cover"
          />
          <button
            type="button"
            onClick={() => onChange(null)}
            className="text-xs text-fg-muted hover:text-danger"
          >
            Remove
          </button>
        </div>
      ) : null}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        {...(capture ? { capture: 'environment' as const } : {})}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) void onPick(file)
          e.target.value = ''
        }}
      />
      <Button
        type="button"
        variant="secondary"
        size="sm"
        loading={busy}
        onClick={() => inputRef.current?.click()}
      >
        {value ? 'Replace' : label}
      </Button>
      {error ? <p className="text-xs text-danger">{error}</p> : null}
    </div>
  )
}
